<template>
  <div>
    <div v-if="hasAccess">
      <subtitle-collapsible-with-add
        :addButtonVisible="!!links.aanmaken"
        :title="$t('opdrachten.title')"
        @add="navToCreateOpdracht"
      />
      <opdracht-filter @search="search($event)" />

      <div
        v-if="
          (gebruikerInfoService.geschorstDeskD || gebruikerInfoService.ingetrokkenDeskD) &&
          !getAlreadySeenBeperkingDialog()
        "
      >
        <v-dialog id="beperking-erkenning-dialog" v-model="dialog" persistent max-width="700">
          <v-card>
            <v-card-title class="headline">{{ $t('algemeen.erkenning-info.title') }}</v-card-title>
            <v-card-text>
              {{
                $t('algemeen.erkenning-info.information', {
                  status: (gebruikerInfoService.geschorstDeskD
                    ? $t('algemeen.erkenning-info.status.geschorst')
                    : $t('algemeen.erkenning-info.status.ingetrokken')
                  )
                    .toString()
                    .toLowerCase(),
                })
              }}
            </v-card-text>
            <v-card-actions>
              <wizard-button-group
                :back-visible="false"
                :next-text="$t('acties.sluiten')"
                @next="closeBeperkingDialog()"
              />
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>

      <v-row id="opdrachten-row">
        <template v-if="loading">
          <v-col class="d-flex" v-bind="$defaultColWidth" v-for="i in 3" :key="i">
            <v-skeleton-loader class="mb-6 flex" elevation="2" type="article, actions" />
          </v-col>
        </template>
        <template v-else-if="opdrachten.length > 0">
          <v-col
            class="d-flex"
            v-bind="$defaultColWidth"
            v-for="opdracht in opdrachten"
            :key="opdracht.id"
            @click="navToOpdracht(opdracht.id)"
          >
            <opdracht-card :opdracht="opdracht" @click-item="clickOpdrachtItem" @delete-item="loadOpdrachten" />
          </v-col>
        </template>
        <v-col v-else>{{ $t('opdrachten.niet-gevonden') }}</v-col>
      </v-row>
      <pagination
        v-model="opdrachtPagination.page"
        :total-element-count="opdrachtenCount"
        :elements-per-page="opdrachtPagination.size"
        @input="loadOpdrachten()"
      />
    </div>
    <v-container v-else>
      <v-row>
        <v-col>
          {{ notInRolErrorMessage }}
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <primary-button class="text-none" :href="epbUrl">
            {{ $t('errors.ga-naar-epb') }}
          </primary-button>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import SubtitleCollapsibleWithAdd from '@/components/title/SubtitleCollapsibleWithAdd.vue';
import { Component, Inject, Provide, Watch } from 'vue-property-decorator';
import OpdrachtCard from '@/components/card/OpdrachtCard.vue';
import { OpdrachtClass } from '@/domain/interface/opdracht/OpdrachtClass';
import { MenuItemInterface } from '@/domain/interface/intern/MenuItemInterface';
import { mixins } from 'vue-class-component';
import RulesMixin from '@/mixins/RulesMixin';
import AuthService from '@/services/AuthorizationService';
import { LocaleMessage } from 'vue-i18n';
import { namespace } from 'vuex-class';
import PrimaryButton from '@/components/button/PrimaryButton.vue';
import { Paginated } from '@/domain/interface/Paginated';
import Pagination from '@/components/layout/Pagination.vue';
import EnumConstantsMixin from '@/mixins/EnumConstantsMixin';
import OpdrachtFilter from '@/views/partials/filter/OpdrachtFilter.vue';
import ConfigHelper from '@/util/ConfigHelper';
import OpdrachtService from '@/services/OpdrachtService';
import { Filter, PagingInfo } from '@/domain/interface/Filter';
import FilterService from '@/services/FilterService';
import { LinkInterface } from '@/domain/interface/LinkInterface';
import LocalStorageService from '@/services/LocalStorageService';
import { ERKENNING_BEPERKT_DIALOG_SHOWN_KEY } from '@/util/constants/constants';
import GebruikerInfoService from '@/services/GebruikerInfoService';
import WizardButtonGroup from '@/components/button/WizardButtonGroup.vue';

const Loader = namespace('Loader');
const Opdracht = namespace('Opdracht');

const auth = new AuthService();

@Component({
  components: {
    WizardButtonGroup,
    PrimaryButton,
    Pagination,
    OpdrachtCard,
    OpdrachtFilter,
    SubtitleCollapsibleWithAdd,
  },
})
export default class Home extends mixins(RulesMixin, EnumConstantsMixin) {
  @Inject('filterService') filterService: FilterService;
  @Inject('opdrachtService') opdrachtService: OpdrachtService;
  @Inject('localStorageService') localStorageService: LocalStorageService;
  @Inject('gebruikerInfoService') gebruikerInfoService: GebruikerInfoService;

  @Provide() hasAccess = true;
  @Provide() loading = false;
  @Provide() opdrachten: OpdrachtClass[] = [];
  opdrachtPagination: PagingInfo = {} as PagingInfo;
  opdrachtenCount = 0;
  dialog = true;
  links: LinkInterface = {} as LinkInterface;
  searchParams: Filter = {} as Filter;

  created() {
    this.opdrachtPagination = this.filterService.getOpdrachtPagingInfo();
    this.searchParams = this.filterService.getOpdrachtFilter();
    auth
      .isLoggedIn()
      .then((loggedIn) => {
        if (!loggedIn) {
          auth.login();
        }
      });

    this.updateLoaderLoading('');
    auth
      .hasAccess()
      .then((resp) => {
        this.hasAccess = resp;
      })
      .catch(() => {
        this.hasAccess = false;
      })
      .finally(this.updateLoaderDone);
    this.loadOpdrachten();
    this.resetOpdracht();
  }

  @Watch('opdrachtPagination', { deep: true })
  updatePaginationInStore() {
    this.filterService.updateOpdrachtPagingInfo(this.opdrachtPagination);
  }

  navToCreateOpdracht(): void {
    this.$router.push({ name: 'opdrachtAanmaken' });
  }

  navToOpdracht(uuid: string): void {
    this.$router.push({ name: 'opdracht', params: { opdrachtUuid: uuid } });
  }

  clickOpdrachtItem(item: MenuItemInterface): void {
    this.$router.push(item.to);
  }

  public getAlreadySeenBeperkingDialog() {
    return this.localStorageService.getItem<boolean>(ERKENNING_BEPERKT_DIALOG_SHOWN_KEY);
  }

  private closeBeperkingDialog() {
    this.localStorageService.setItem(ERKENNING_BEPERKT_DIALOG_SHOWN_KEY, true);
    this.dialog = false;
  }

  public loadOpdrachten() {
    this.loading = true;
    this.opdrachten = [];
    const pageParams = { limit: this.opdrachtPagination.size, offset: this.opdrachtPagination.page - 1 };

    return this.opdrachtService
      .getOpdrachten(pageParams, this.searchParams)
      .then((page: Paginated<OpdrachtClass>) => {
        this.opdrachtenCount = page.count;
        page.data.forEach((opdracht) => {
          this.opdrachten.push(new OpdrachtClass(opdracht));
        });
        this.links = page.links;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public search(params: Filter) {
    this.loading = true;
    this.updateLoaderLoading('Even geduld...');
    this.searchParams = params;
    this.opdrachtPagination.page = 1;
    this.filterService.updateOpdrachtFilter(JSON.parse(JSON.stringify(this.searchParams)));
    this.loadOpdrachten().finally(() => {
      this.updateLoaderDone();
    });
  }

  get notInRolErrorMessage() {
    const toepassingsnaam = ConfigHelper.isLeeromgeving() ? this.$t('app.leeromgeving') : this.$t('app.title');

    return this.$t('errors.geen-toegang', {
      rol: this.rolOmschrijving,
      toepassingsnaam: toepassingsnaam,
    });
  }

  get rolOmschrijving() {
    if (ConfigHelper.isRolDeskundigeD()) {
      return this.$t('rol.energiedeskundige-type-d');
    } else if (ConfigHelper.isRolStudentTypeD()) {
      return this.$t('rol.student-type-d');
    } else {
      return '';
    }
  }

  get epbUrl() {
    return process.env.VUE_APP_EPB_URL;
  }

  @Loader.Action
  public updateLoaderLoading!: (message?: string | LocaleMessage) => void;

  @Loader.Action
  public updateLoaderDone!: () => void;

  @Opdracht.Action
  public resetOpdracht!: () => void;
}
</script>
